import React, { useContext, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import { Col } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";

import { MdOutlineModeEditOutline, MdPlace } from "react-icons/md";
import { BsClockFill, BsFillCalendarEventFill, BsCalendarEvent } from "react-icons/bs";

import SelectPcp from "../../forms/SelectPcp";
import TextArea from "../../../shared/TextArea";
import FieldError from "../../../shared/FieldError";
import SelectVisitType from "../../forms/SelectVisitType";
import Lead from "../../../shared/Lead";

import * as Utils from "../../../helpers/Utility";
import { Settings, Task } from "../../../helpers/Contexts";

const Schedule = ({ appointment, formHandler, children }) => {
  const settings = useContext(Settings);
  const { taskValues, setTaskValues } = useContext(Task);

  const ScheduleSchema = Yup.object().shape({
    appointment_time: Yup.string().matches(
      "([01]?[0-9]|2[0-3]):[0-5][0-9]",
      "Appointment time must be in a valid 12 hour (e.g HH:MM AM/PM) or military (e.g. 13:00) format. "
    ),
    appointment_date: Yup.date().required("Appointment date is required."),
    procedure_type: Yup.string().required("Visit type is required"),
    pcp: Yup.string(),
  });

  return (
    <>
      <Formik
        initialValues={{
          notes: appointment.notes ? appointment.notes : "",
          procedure_type_id: appointment.procedure_type_id,
          procedure_type: appointment.procedure_type,
          pcp: appointment.cpr.servicing_provider ? appointment.cpr.servicing_provider : "",
          pcp_id: appointment.cpr.servicing_provider_id
            ? appointment.cpr.servicing_provider_id
            : "",
          pcp_practice_id: appointment.pcp_practice ? appointment.pcp_practice.id : "",
          pcp_practice_address: appointment.pcp_practice ? appointment.pcp_practice.address : "",
          pcp_practice_name: appointment.pcp_practice ? appointment.pcp_practice.name : "",
          date: appointment.cpr.appointment_date
            ? appointment.cpr.appointment_date
            : appointment.request_date_start,
          appointment_date: appointment.cpr.appointment_date
            ? appointment.cpr.appointment_date
            : appointment.request_date_start,
          appointment_time: appointment.cpr.appointment_time
            ? Utils.ConvertToPacificTime(appointment.cpr.appointment_time)
            : "",
        }}
        validationSchema={ScheduleSchema}
        validateOnChange={true}
        onSubmit={(values) => {
          formHandler({
            task: {
              id: appointment.id,
              priority: null,
              appointment_time: Utils.ConvertTime(values.appointment_time),
              order_date: new Date(),
              appointment_source: "vip_hotline",
              appointment_request_status: "appointment_confirmed",
              appointment_date: values.appointment_date,
              procedure_type_id: values.procedure_type_id,
              servicing_provider: values.pcp,
              notes: values.notes,
              pcp_practice_id: values.pcp_practice_id,
              assigner_id: settings.me.id,
            },
          });
        }}
      >
        {({ values, setFieldValue, errors, touched }) => {
          return (
            <>
              <Col
                lg={
                  appointment.appointment_request_status == "visit_status_check" ||
                  appointment.appointment_request_status == "appointment_completed" ||
                  appointment.appointment_request_status == "appointment_not_completed"
                    ? 4
                    : appointment.appointment_request_status == "appointment_request_created" &&
                      !taskValues.editing.scheduled
                    ? null
                    : 6
                }
                sm={
                  appointment.appointment_request_status == "appointment_request_created" &&
                  !taskValues.editing.scheduled
                    ? null
                    : 12
                }
                className={
                  appointment.appointment_request_status == "appointment_request_created" &&
                  !taskValues.editing.scheduled
                    ? null
                    : "appointment"
                }
              >
                <Form>
                  {taskValues.editing.scheduled && (
                    <>
                      {" "}
                      <MdOutlineModeEditOutline id="pencil-icon" />
                      <Lead
                        message={
                          appointment.appointment_request_status == "appointment_request_created"
                            ? "Scheduling Appointment"
                            : "Editing Appointment Scheduled"
                        }
                      />
                      {children} <br />
                      <BsCalendarEvent id="appointment-big-icon" />
                      <br />
                      <SelectVisitType
                        name="procedure_type"
                        value={values.procedure_type_id}
                        setFieldValue={setFieldValue}
                      />
                      <label>{"Servicing Provider"}</label>
                      {values.procedure_type == "Lab/Diagnostic Only" ? (
                        <select disabled className="dropdown-select">
                          <option value={""}>{"N/A for Lab/Diagnostic Only"}</option>
                        </select>
                      ) : (
                        <SelectPcp name="pcp" required={true} setFieldValue={setFieldValue} />
                      )}
                      <label>Appointment Date & Time</label>
                      <div className="flex">
                        <div className={"flex datefield-wrapper"}>
                          <Field name="date">
                            {({ field, meta }) => {
                              return (
                                <>
                                  <BsFillCalendarEventFill className="calendar-icon" />
                                  <ReactDatePicker
                                    {...field}
                                    autoComplete="off"
                                    className="datepicker"
                                    dateFromat={"YYYY-MM-dd"}
                                    onChange={(date) => {
                                      setFieldValue("appointment_date", date);
                                      setFieldValue("date", date.toISOString().slice(0, 10));
                                    }}
                                  />
                                </>
                              );
                            }}
                          </Field>
                        </div>
                        <div className={"flex datefield-wrapper"}>
                          <BsClockFill className="calendar-icon" />
                          <Field name="appointment_time" className="datepicker" />
                          <br />{" "}
                        </div>
                      </div>
                      <div className="mt10">
                        <Col md={6} id="field-left">
                          {errors.appointment_date ? (
                            <FieldError error={errors.appointment_date} />
                          ) : null}
                        </Col>
                        <Col md={6} id="field-right">
                          {errors.appointment_time ? (
                            <FieldError error={errors.appointment_time} />
                          ) : null}
                        </Col>
                      </div>
                      <TextArea
                        label="Notes"
                        name="notes"
                        placeholder="Please write additional notes"
                      />
                      <button id="btn outline" type="submit">
                        Submit Appointment Scheduled
                      </button>
                    </>
                  )}
                </Form>

                {!taskValues.editing.scheduled &&
                  appointment.appointment_request_status !== "appointment_request_created" && (
                    <>
                      <Lead message={" Appointment Scheduled "} />
                      {children}
                      <br />
                      <BsCalendarEvent id="appointment-big-icon" />
                      <br />
                      <label>{"Visit Type"} </label>
                      {values.procedure_type && values.procedure_type}
                      <label>{"Servicing Provider"}</label>{" "}
                      {values.procedure_type == "Lab/Diagnostic Only"
                        ? "N/A for Lab/Diagnostic Only"
                        : values.pcp}
                      {values.pcp_practice_id && (
                        <>
                          <label>Location</label>
                          <MdPlace className="pcp-icon" />
                          {values.pcp_practice_name}
                          <br />
                          {values.pcp_practice_address}
                        </>
                      )}
                      <div className="mt10 mb10">
                        <label>Appointment Date & Time </label>
                        <BsFillCalendarEventFill className="cpr-label-icon" />
                        <b>{" " + values.date + " "}</b>
                        <BsClockFill className="cpr-label-icon" />
                        <b>{values.appointment_time}</b> {"  "}
                          (Pacific Standard/Daylight Time)
                      </div>
                      {values.notes && (
                        <>
                          <label>Notes</label>
                          {values.notes}
                        </>
                      )}
                      {appointment.cpr.appointment_initiated_date ? (
                        <>
                          <label>{"Appointment Initated Date"}</label>
                          {appointment.cpr.appointment_initiated_date}
                        </>
                      ) : (
                        ""
                      )}
                      {appointment.appointment_source && (
                        <>
                          <label>{"Appointment Source"}</label>{" "}
                          {appointment.appointment_source
                            .replaceAll("_", " ")
                            .replaceAll("-", " ")
                            .toUpperCase()}
                        </>
                      )}
                      <div className="bottom-right" id="from">
                        {new Date(appointment.cpr.created_at).toLocaleString([], {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </>
                  )}
              </Col>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default Schedule;
